// MCIP-29 v2 — "Suite changes" panel on the TRENDS screen (moved off the job
// view, Aug 11 review: the bar chart read as noise there and long Java names
// overflowed the card).
//
// Design rules:
//  - Text lists + delta CHIPS, not a fifth chart — the trends screen's
//    four-graphics budget holds (same precedent as the impact panel).
//  - Names show the matchable tail (`Class.method`, full FQN in the tooltip)
//    and can never leak out of the container.
//  - Every new failing case links to ITS signal (?job=…&cluster=…) — the
//    insight is one click from the fix card that explains it.
import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { runName, shortTestName } from '../../lib/format';

const LIFECYCLE_LABEL = {
  chronic: 'keeps failing',
  intermittent: 'flaky',
  settled: 'settled',
  'one-off': 'one-off',
  'too-new': 'too new to judge',
};

function VerdictChip({ quality }) {
  if (!quality) return null;
  if (quality.insufficient_history) {
    return <span className="chip" title={`needs more than ${quality.baseline_runs} harvested runs`}>not enough history</span>;
  }
  const cls = quality.verdict === 'healthy' ? 'chip--ok' : quality.verdict === 'watch' ? 'chip--warn' : 'chip--bad';
  return <span className={`chip ${cls}`} title={quality.reason}>new cases: {quality.verdict}</span>;
}

/** Per-run delta chips, oldest → newest: "+4" / "−6" / "±0", date underneath. */
function DeltaStrip({ series }) {
  const runs = series.filter((s) => s.delta !== null);
  if (!runs.length) return <p className="muted">not enough runs to diff case counts</p>;
  return (
    <div className="suitechanges__strip" role="list" aria-label="Cases added or removed per run">
      {runs.map((s) => (
        <span
          key={s.run_id}
          role="listitem"
          className={`suitechanges__delta${s.delta > 0 ? ' is-add' : s.delta < 0 ? ' is-remove' : ''}`}
          title={`${runName(s)} — ${s.executed} cases executed`}
        >
          <b>{s.delta > 0 ? `+${s.delta}` : s.delta === 0 ? '±0' : s.delta}</b>
          <small>{runName(s)}</small>
        </span>
      ))}
    </div>
  );
}

function NewCaseRow({ t, jobId }) {
  const signal = t.cluster_id && jobId
    ? `/?job=${encodeURIComponent(jobId)}&cluster=${encodeURIComponent(t.cluster_id)}`
    : null;
  return (
    <li className="suitechanges__row">
      <span className={`tag${t.lifecycle === 'chronic' ? ' tag--bug' : ''}`} title={`failed in ${t.failed_runs} run${t.failed_runs === 1 ? '' : 's'} since it appeared`}>
        {LIFECYCLE_LABEL[t.lifecycle] || t.lifecycle}
      </span>
      <code className="suitechanges__name" title={t.test_name}>{shortTestName(t.test_name)}</code>
      {signal
        ? <a className="run-link suitechanges__link" href={signal}>view signal ↗</a>
        : <span className="muted suitechanges__link">no signal yet</span>}
    </li>
  );
}

function PodDrill({ pod, region, environment }) {
  const [state, setState] = useState('loading');
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setState('loading');
    api.podSuiteGrowth(pod, { region, environment })
      .then((d) => { if (!cancelled) { setData(d); setState('done'); } })
      .catch(() => { if (!cancelled) setState('error'); });
    return () => { cancelled = true; };
  }, [pod, region, environment]);

  if (state === 'loading') return <p className="muted">Loading pod suite changes…</p>;
  if (state === 'error') return <p className="notice notice--error">Couldn't load the pod drill.</p>;
  if (!data.jobs.length) return <p className="muted">No {pod} jobs in this slice.</p>;
  return (
    <div className="card__table">
      <table className="data-table">
        <thead>
          <tr><th>job</th><th className="num">net cases</th><th className="num">added</th><th className="num">removed</th><th className="num">new failing</th><th>new-case verdict</th></tr>
        </thead>
        <tbody>
          {data.jobs.map((j) => (
            <tr key={j.job_id}>
              <td><a className="run-link" href={`/trends?job=${encodeURIComponent(j.job_id)}`}>{j.job_id}</a></td>
              <td className="num">{j.totals.net_cases === null ? '—' : (j.totals.net_cases >= 0 ? `+${j.totals.net_cases}` : j.totals.net_cases)}</td>
              <td className="num">{j.totals.added}</td>
              <td className="num">{j.totals.removed}</td>
              <td className="num">{j.new_case_quality.new_failing_tests}</td>
              <td><VerdictChip quality={j.new_case_quality} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function SuiteChangesPanel({ jobId, pod, region, environment }) {
  const [growth, setGrowth] = useState(null);
  const [drillOpen, setDrillOpen] = useState(false);
  const fetchSeq = useRef(0);

  useEffect(() => {
    if (!jobId) { setGrowth(null); return; }
    const seq = ++fetchSeq.current;
    setDrillOpen(false);
    api.suiteGrowth(jobId)
      .then((g) => { if (seq === fetchSeq.current) setGrowth(g); })
      .catch(() => { if (seq === fetchSeq.current) setGrowth(null); }); // enhancement panel: absent on failure
  }, [jobId]);

  if (!growth) return null;

  const q = growth.new_case_quality;
  const net = growth.totals.net_cases;

  return (
    <section className="card suitechanges">
      <header className="card__head">
        <div>
          <h2 className="card__title">Suite changes · new-case quality</h2>
          <p className="card__subtitle">
            cases executed per run, diffed — {growth.totals.runs} runs ·{' '}
            net {net === null ? '—' : net >= 0 ? `+${net}` : net}
            {growth.totals.added > 0 && ` · +${growth.totals.added} added`}
            {growth.totals.removed > 0 && ` · −${growth.totals.removed} removed`}
            {' '}· are the added cases holding up?
          </p>
        </div>
        <VerdictChip quality={q} />
      </header>

      <DeltaStrip series={growth.series} />

      {q.new_failing_tests > 0 && (
        <>
          <h3 className="detail__h3">
            New failing cases — first failure after the job's baseline ({q.new_failing_tests})
          </h3>
          <ul className="suitechanges__tests">
            {q.tests.slice(0, 8).map((t) => (
              <NewCaseRow key={`${t.suite}-${t.test_name}`} t={t} jobId={jobId} />
            ))}
          </ul>
        </>
      )}
      {!q.insufficient_history && q.new_failing_tests === 0 && (
        <p className="muted suitechanges__quiet">
          no new case has failed since the baseline — additions in this window are holding up
        </p>
      )}

      {pod && (
        <p className="suitechanges__more">
          <button type="button" className="link-btn" onClick={() => setDrillOpen((v) => !v)}>
            {drillOpen ? 'hide' : 'view'} {pod} pod suite changes {drillOpen ? '↑' : '→'}
          </button>
        </p>
      )}
      {drillOpen && <PodDrill pod={pod} region={region} environment={environment} />}
    </section>
  );
}
